import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import {
  getVimeoVideoDetails,
  getVimeoVideoTextTracks,
  upsertVideoVectorToPinecone,
} from '../../../../models/videos.js';

// Todo: Permitir reproducir el video y dejar que solo el administrador suba a Pinecone.

const formatDuration = seconds => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds - minutes * 60;
  return `${minutes < 10 ? `0${minutes}` : minutes}:${secs < 10 ? `0${secs}` : secs}`;
};

const formatDate = dateString => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const VideoResourceDetail = ({ videoId, backPath }) => {
  const navigate = useNavigate();

  const [videoDetails, setVideoDetails] = useState(null);
  const [textTracks, setTextTracks] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const [details, tracks] = await Promise.all([
          getVimeoVideoDetails(videoId),
          getVimeoVideoTextTracks(videoId),
        ]);
        if (mounted) {
          setVideoDetails(details);
          setTextTracks(tracks);
        }
      } catch (err) {
        if (mounted) {
          setError(
            err?.message || 'No se pudieron cargar los detalles del video. Intenta nuevamente.'
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetchData();
    return () => {
      mounted = false;
    };
  }, [videoId]);

  const handleBack = () => {
    navigate(backPath);
  };

  const handleUploadToPinecone = async () => {
    if (!videoDetails || !videoId) {
      setError('No hay información del video para subir.');
      return;
    }

    try {
      setIsUploading(true);
      setSuccessMessage('');
      setError('');

      await upsertVideoVectorToPinecone({
        vimeoId: videoId,
        videoName: videoDetails.name || 'Video',
        vttContent: textTracks || '',
        videoDuration: videoDetails.duration || null,
      });

      setSuccessMessage('Vectores del video subidos a Pinecone correctamente.');
    } catch (err) {
      setError(err.message || 'Error al subir video a Pinecone.');
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !videoDetails) {
    return (
      <Box>
        <Box display='flex' justifyContent='flex-end' mb={3}>
          <Button variant='contained' onClick={handleBack} startIcon={<ArrowBackIosIcon />}>
            Volver
          </Button>
        </Box>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );
  }

  if (!videoDetails) {
    return (
      <Box>
        <Box display='flex' justifyContent='flex-end' mb={3}>
          <Button variant='contained' onClick={handleBack} startIcon={<ArrowBackIosIcon />}>
            Volver
          </Button>
        </Box>
        <Alert severity='info'>No se encontró información del video.</Alert>
      </Box>
    );
  }

  const thumbnail = videoDetails.pictures?.sizes?.[videoDetails.pictures.sizes.length - 1]?.link;

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Button variant='outlined' onClick={handleBack} startIcon={<ArrowBackIosIcon />}>
          Volver
        </Button>
        <Button
          variant='contained'
          disabled={isUploading || !videoDetails}
          onClick={handleUploadToPinecone}>
          {isUploading ? 'Subiendo...' : 'Subir a Pinecone'}
        </Button>
      </Box>

      {successMessage && (
        <Alert severity='success' sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant='outlined'>
        {thumbnail && (
          <Box
            component='img'
            src={thumbnail}
            alt={videoDetails.name}
            sx={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'contain',
            }}
          />
        )}
        <CardContent>
          <Typography variant='h4' gutterBottom>
            {videoDetails.name}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Typography variant='subtitle2' color='text.secondary' gutterBottom>
              Descripción
            </Typography>
            <Typography variant='body1'>
              {videoDetails.description || 'Sin descripción disponible.'}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' color='text.secondary'>
                Duración
              </Typography>
              <Typography variant='body1'>{formatDuration(videoDetails.duration)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' color='text.secondary'>
                Fecha de publicación
              </Typography>
              <Typography variant='body1'>{formatDate(videoDetails.release_time)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' color='text.secondary'>
                Reproducciones
              </Typography>
              <Typography variant='body1'>
                {videoDetails.stats?.plays?.toLocaleString() || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant='subtitle2' color='text.secondary'>
                Idioma
              </Typography>
              <Typography variant='body1'>{videoDetails.language || 'N/A'}</Typography>
            </Grid>
            {videoDetails.link && (
              <Grid item xs={12}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                  Enlace de Vimeo
                </Typography>
                <Link href={videoDetails.link} target='_blank' rel='noopener noreferrer'>
                  {videoDetails.link}
                </Link>
              </Grid>
            )}
          </Grid>

          {textTracks && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                  Subtítulos (VTT)
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    p: 2,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                  }}>
                  <Typography
                    variant='body2'
                    component='pre'
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}>
                    {textTracks || 'No hay subtítulos disponibles.'}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VideoResourceDetail;
